import { RefreshCcw05 } from '@untitled-ui/icons-react';
import { twMerge } from 'tailwind-merge';

import {
  type Currency,
  type OrganizationBilling,
  PlanStatus,
  SubscriptionPaymentCycle,
} from '../entity/api/organization-billing';
import { i18n, tNamespace } from '../services/translation';
import { formatCurrency } from '../shared/currency';
import { formatDateToBasedOnLanguage, formatDateToDdMmYyyy } from '../shared/date';
import { calculateMonthlyPrice } from '../shared/subscription';
import { Badge } from './badge';
import { Button } from './buttons/button';

const t = tNamespace('components.current-plan-card');
const tBilling = tNamespace('app.billing.general');
const tSettings = tNamespace('app.settings.organization.billing');

function returnRenewsOnText(props: {
  planStatus: PlanStatus;
  endDate: number;
  paymentCycle: SubscriptionPaymentCycle;
  yearlyPrice?: number;
  currency: Currency;
  userLanguage?: string;
}) {
  const { planStatus, endDate, paymentCycle, yearlyPrice, currency, userLanguage } = props;

  if (planStatus === PlanStatus.Trialing) {
    return t('freeTrialEndsOn', {
      date: formatDateToBasedOnLanguage(new Date(endDate), i18n.language),
    });
  }

  if (planStatus === PlanStatus.Subscribed || planStatus === PlanStatus.Switched) {
    if (paymentCycle === SubscriptionPaymentCycle.Year) {
      return (
        t('billedAnnually', { price: formatCurrency(yearlyPrice ?? 0, currency, userLanguage) }) +
        ' · ' +
        t('renewsOn', { date: formatDateToDdMmYyyy(new Date(endDate)) })
      );
    }
    return t('renewsOn', { date: formatDateToDdMmYyyy(new Date(endDate)) });
  }

  if (planStatus === PlanStatus.Cancelling) {
    return (
      <span
        className="font-body-3-r text-red-600"
        dangerouslySetInnerHTML={{
          __html: t('accessExpiresOn', {
            date: formatDateToDdMmYyyy(new Date(endDate)),
          }),
        }}
      />
    );
  }

  return null;
}

type CurrentPlanCardExtendedProps = {
  organizationBilling: OrganizationBilling;
  userLanguage?: string;
  onPaymentMethodClick?: () => void;
  onInvoicesClick?: () => void;
  onManagePlanClick?: () => void;
  onUncancelPlanClick?: () => void;
  onAbortDowngradeClick?: () => void;
  paymentMethodLoading?: boolean;
  invoicesLoading?: boolean;
  uncancelPlanLoading?: boolean;
  className?: string;
};

export function CurrentPlanCardExtended(props: CurrentPlanCardExtendedProps) {
  const {
    organizationBilling,
    userLanguage,
    onPaymentMethodClick,
    onInvoicesClick,
    onManagePlanClick,
    onUncancelPlanClick,
    onAbortDowngradeClick,
    paymentMethodLoading,
    invoicesLoading,
    uncancelPlanLoading,
    className,
  } = props;

  const { currentPlanStatus, currentPlanRecurring, currentCurrency, currentPlanEndDate, plan } =
    organizationBilling;

  let originalMonthly = plan?.pricePerMonth ?? 0;
  if (originalMonthly === 0) {
    // LEGACY: these are no longer used
    originalMonthly = organizationBilling.currentOriginalMonthlyPriceAmount;
  }

  let currentMonthly = calculateMonthlyPrice(originalMonthly, plan?.priceDiscount ?? 0);
  if (currentMonthly === 0) {
    // LEGACY: these are no longer used
    currentMonthly = organizationBilling.currentMonthlyPriceAmount;
  }

  const yearlyPrice = currentMonthly * 12;

  return (
    <div
      className={twMerge(
        'relative flex min-h-[120px] w-full flex-col rounded-lg border border-branding-accent-600 bg-branding-accent-50 p-6 pt-8',
        currentPlanStatus === PlanStatus.PastDue || currentPlanStatus === PlanStatus.Cancelling
          ? 'border-red-600 bg-red-50'
          : '',
        className
      )}
    >
      {/* Floating Badge at Top Center */}
      {plan != null ? (
        <Badge
          size="sm"
          color={currentPlanStatus === PlanStatus.Cancelling ? 'red-contrast' : 'accent-contrast'}
          className="absolute left-[52px] top-0 -translate-y-1/2 uppercase"
        >
          {currentPlanStatus === PlanStatus.Cancelling ? (
            tBilling('cancelledPlanBadge')
          ) : (
            <>
              <span>{plan.kind}</span>
              <span className="text-branding-accent-200">{tBilling('plan')}</span>
            </>
          )}
        </Badge>
      ) : null}

      {/* Main Content Container - Horizontal Layout */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
          {/* Left Section - Plan Info */}
          <div className="flex w-full flex-1 flex-col gap-4">
            {/* Pricing */}
            <div className="flex flex-col gap-1">
              <div className="flex h-full items-center gap-1">
                {currentMonthly < originalMonthly ? (
                  <p className="font-display-4 text-gray line-through">
                    {formatCurrency(originalMonthly, currentCurrency, userLanguage)}
                  </p>
                ) : null}
                <p className="font-display-2">
                  {formatCurrency(currentMonthly, currentCurrency, userLanguage)}
                </p>
                <p className="font-body-2-r text-dark-gray">{t('perMonth')}</p>
              </div>
              {/* Trial Badge */}
              {currentPlanStatus === PlanStatus.Trialing ? (
                <Badge size="sm" color="greenDarker" className="w-fit">
                  {t('trial')}
                </Badge>
              ) : null}

              {/* Renews On */}
              {currentPlanStatus === PlanStatus.PastDue ? (
                <Badge size="sm" color="redDarker" className="w-fit">
                  {t('paymentFailed')}
                </Badge>
              ) : (
                <p className="font-body-3-r text-dark-gray">
                  {returnRenewsOnText({
                    planStatus: currentPlanStatus,
                    endDate: currentPlanEndDate,
                    paymentCycle: currentPlanRecurring,
                    currency: currentCurrency,
                    yearlyPrice,
                    userLanguage,
                  })}
                </p>
              )}
            </div>
            {/* Bottom Section - Manage Plan Button */}
            {onManagePlanClick != null &&
            (currentPlanStatus === PlanStatus.Trialing ||
              currentPlanStatus === PlanStatus.Subscribed) ? (
              <Button size="sm" variant="outline" onClick={onManagePlanClick} className="w-fit">
                {tBilling('managePlan')}
              </Button>
            ) : null}
            {onAbortDowngradeClick != null && currentPlanStatus === PlanStatus.Switched ? (
              <Button size="sm" variant="outline" onClick={onAbortDowngradeClick} className="w-fit">
                {tSettings('cancelDowngradeButton')}
              </Button>
            ) : null}
            {onUncancelPlanClick != null && currentPlanStatus === PlanStatus.Cancelling ? (
              <Button
                size="sm"
                variant="accent"
                onClick={onUncancelPlanClick}
                className="w-fit"
                leftIcon={<RefreshCcw05 width={16} height={16} />}
                loading={uncancelPlanLoading}
              >
                {tBilling('uncancelPlan')}
              </Button>
            ) : null}
          </div>

          {/* Right Section - Action Buttons */}
          <div className="flex items-center gap-3">
            {currentPlanStatus !== PlanStatus.Cancelling ? (
              <>
                <Button
                  size="sm"
                  variant={currentPlanStatus === PlanStatus.PastDue ? 'destructive' : 'ghost-gray'}
                  onClick={onPaymentMethodClick}
                  className="whitespace-nowrap"
                  loading={paymentMethodLoading}
                >
                  {currentPlanStatus === PlanStatus.PastDue
                    ? tBilling('updatePaymentMethod')
                    : tBilling('paymentMethod')}
                </Button>
                <div className="h-5 w-[1px] bg-branding-accent-300" />
              </>
            ) : null}
            <Button
              size="sm"
              variant="ghost-gray"
              onClick={onInvoicesClick}
              className="whitespace-nowrap"
              loading={invoicesLoading}
            >
              Invoices
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
